import './styles.css';
import { useState, useRef, useEffect, memo, useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import {
  View,
  Flex,
} from '@smarterservices/smarter-ui';
import Communication from './Communication';
import CheckinV1 from './CheckInV1';
import SessionInfo from './SessionInfo';
import Journey from './Journey';
import AnnotationsList from './Annotations';
import ComputerInfo from './ComputerInfo';
import Identity from './Identity';
import IncidentReport from './IncidentReport';
import CustomTab from './CustomTab';
import { CommunicationTab, IncidentReportTab, IdentificationTab } from './Tabs';
import NavBar from './Tabs/NavBar';
import { getStore, useAppStore, useExamSessionStore } from '../../stores/StoreContext';

/**
 * SessionDetails
 *
 * This component displays the details of a session, including the session
 * information, check-in, interactions, chat, and journey.
 *
 * @returns {JSX.Element} The component.
 */
const SessionDetails = () => {
  const LayoutStore = getStore('layoutStore');
  const EventStore = getStore('eventStore');

  //const { height } = useSecondPaneContext();

  const navigationHeight = 40;

  // Get selected tab from AppStore
  const selectedNavItem = useAppStore((state) => state.sessionDetailsTab);
  const setSelectedNavItem = useAppStore((state) => state.setSessionDetailsTab);

  // Get sessionDetails configuration (can be boolean or object)
  // Use shallow comparison to prevent unnecessary re-renders when object reference changes but values are the same
  const sessionDetails = useAppStore(
    (state) => state.enabledComponents.sessionDetails,
    shallow
  );

  // Parse sessionDetails configuration with stable reference
  // Store the config in a ref and only update when values actually change
  const sessionDetailsConfigRef = useRef(null);
  const sessionDetailsConfig = useMemo(() => {
    const newConfig = !sessionDetails ? null :
      typeof sessionDetails === 'boolean'
        ? { enabled: true, defaultTab: null, tabs: null }
        : {
          enabled: true,
          defaultTab: sessionDetails.defaultTab || null,
          tabs: sessionDetails.tabs || null
        };

    // Only update if values actually changed
    const prev = sessionDetailsConfigRef.current;
    if (!prev && !newConfig) return null;
    if (!prev || !newConfig) {
      sessionDetailsConfigRef.current = newConfig;
      return newConfig;
    }

    // Deep comparison of values
    const hasChanged =
      prev.enabled !== newConfig.enabled ||
      prev.defaultTab !== newConfig.defaultTab ||
      JSON.stringify(prev.tabs) !== JSON.stringify(newConfig.tabs);

    if (hasChanged) {
      sessionDetailsConfigRef.current = newConfig;
      return newConfig;
    }

    // Return the same reference if nothing changed
    return prev;
  }, [sessionDetails]);

  // Apply default tab from configuration only on initial mount
  const hasInitializedDefaultTab = useRef(false);


  // Track which tabs have been mounted for lazy loading with state preservation
  // Tabs are only rendered when first visited, then kept mounted but hidden
  const [mountedTabs, setMountedTabs] = useState(new Set());
  useEffect(() => {
    // Only set default tab once on mount, don't override user selections
    if (!hasInitializedDefaultTab.current && sessionDetailsConfig) {
      if (sessionDetailsConfig.defaultTab) {
        setSelectedNavItem(sessionDetailsConfig.defaultTab);
      } else if (!selectedNavItem) {
        // Fallback to 'info' if no defaultTab specified and no tab selected
        setSelectedNavItem('info');
      }
      hasInitializedDefaultTab.current = true;
    }
  }, [sessionDetailsConfig, selectedNavItem, setSelectedNavItem]);

  // Get incident report state early so it can be used in effects
  const incidentReport = useExamSessionStore((state) => state.incidentReport);

  // Ensure a tab is always selected - default to 'info' if none selected
  useEffect(() => {
    if (!selectedNavItem) {
      setSelectedNavItem('info');
    }
  }, [selectedNavItem, setSelectedNavItem]);

  // Check if selected tab is still available, default to 'info' if removed/hidden
  // This only runs when the configuration or incident report changes, NOT when user clicks a tab
  useEffect(() => {
    if (selectedNavItem && sessionDetailsConfig) {
      // Build the list of available tabs
      let availableTabs;
      if (sessionDetailsConfig.tabs) {
        availableTabs = sessionDetailsConfig.tabs.map(tab =>
          typeof tab === 'string' ? tab : tab.id
        );
      } else {
        availableTabs = ['info', 'flags', 'identity', 'checkin', 'communication'];
      }

      // Check if incident report should be included
      if (incidentReport && !availableTabs.includes('incidentReport')) {
        availableTabs.push('incidentReport');
      } else if (!incidentReport) {
        availableTabs = availableTabs.filter(tab => tab !== 'incidentReport');
      }

      // If selected tab is not in available tabs, default to 'info'
      // This prevents the tab from being reset when user actively clicks it
      if (!availableTabs.includes(selectedNavItem)) {
        setSelectedNavItem('info');
      }
    }
  }, [sessionDetailsConfig, incidentReport, setSelectedNavItem]);

  // Lazy load tabs: Add selected tab to mounted set when it's first visited
  useEffect(() => {
    if (selectedNavItem && !mountedTabs.has(selectedNavItem)) {
      setMountedTabs(prev => new Set([...prev, selectedNavItem]));
    }
  }, [selectedNavItem, mountedTabs]);

  // Trigger event when SessionDetails is loaded and ready for interaction
  const hasTriggeredLoadedEvent = useRef(false);
  useEffect(() => {
    // Only trigger once when sessionDetailsConfig is available and tab is set
    if (!hasTriggeredLoadedEvent.current && sessionDetailsConfig && selectedNavItem) {
      EventStore.trigger('onSessionDetailsLoaded', {
        config: sessionDetailsConfig,
        currentTab: selectedNavItem
      });
      hasTriggeredLoadedEvent.current = true;
    }
  }, [sessionDetailsConfig, selectedNavItem, EventStore]);

  // Handle showing/hiding the pane when sessionDetailsConfig changes
  useEffect(() => {

    if (LayoutStore.detailsSplitView) {
      if (!sessionDetailsConfig) {
        // Hide the second pane and divider
        LayoutStore.detailsSplitView.hideSecondPane();
        LayoutStore.detailsSplitView.hideDivider();
      } else {
        // Show the second pane and divider
        LayoutStore.detailsSplitView.showSecondPane();
        LayoutStore.detailsSplitView.showDivider();
      }
    }
  }, [sessionDetailsConfig]);

  // Handle nav item selection with useCallback to prevent unnecessary re-renders
  const handleNavItemClick = useCallback(
    (key) => {
      // Update the selected tab
      setSelectedNavItem(key);

      // Only expand the split view when a tab is explicitly clicked
      // This won't run on re-renders, only on actual click events
      if (
        LayoutStore.detailsSplitView &&
        LayoutStore.detailsSplitView.isCollapsed
      ) {
        LayoutStore.detailsSplitView.expand();
      }
    },
    [LayoutStore.detailsSplitView?.isCollapsed]
  );

  // Memoize the navigation options to prevent unnecessary re-renders
  const navOptions = useMemo(
    () => ({
      info: {
        label: 'Session Info',
        notification: { color: 'danger' },
        component: <SessionInfo />,
      },
      checkin: {
        label: 'Check-in',
        notification: { color: 'danger' },
        component: <CheckinV1 />,
      },
      communication: {
        label: 'Chat',
        notification: { color: 'danger', count: 0, showValue: true },
        // Use a function to create the tab component dynamically
        renderTab: (key, option, isSelected, onClick) => (
          <CommunicationTab
            key={key}
            tabKey={key}
            option={option}
            isSelected={isSelected}
            onClick={onClick}
          />
        ),
        component: <Communication />
      },
      identity: {
        label: 'Identification',
        notification: { color: 'danger' },
        component: <Identity />,
        renderTab: (key, option, isSelected, onClick) => (
          <IdentificationTab
            key={key}
            tabKey={key}
            option={option}
            isSelected={isSelected}
            onClick={onClick}
          />
        ),
      },
      flags: {
        label: 'Flags',
        notification: { color: 'danger' },
        component: <AnnotationsList />,
      },

      incidentReport: {
        label: 'Incident Report',
        component: <IncidentReport />,
        notification: { color: 'danger', count: 0, showValue: true },
        // Use a function to create the tab component dynamically
        renderTab: (key, option, isSelected, onClick) => (
          <IncidentReportTab
            key={key}
            tabKey={key}
            option={option}
            isSelected={isSelected}
            onClick={onClick}
          />
        ),
      },
      journey: {
        label: 'Journey',
        notification: { color: 'danger' },
        component: <Journey />,
      },
      computerInfo: {
        label: 'Computer Info',
        notification: { color: 'danger' },
        component: <ComputerInfo />,
      },
    }),
    []
  ); // Empty dependency array since these don't change


  // Memoize the content components to prevent unnecessary re-renders
  const contentComponents = useMemo(() => {
    // Get the list of tabs to display (same logic as navItems)
    let itemsToDisplay;
    if (sessionDetailsConfig?.tabs) {
      itemsToDisplay = sessionDetailsConfig.tabs.map(tab =>
        typeof tab === 'string' ? tab : tab.id
      );
    } else {
      itemsToDisplay = ['info', 'flags', 'identity', 'checkin', 'communication'];
    }

    // Only show incident report tab if there is one...
    if (incidentReport && !itemsToDisplay.includes('incidentReport')) {
      itemsToDisplay.push('incidentReport');
    } else if (!incidentReport) {
      itemsToDisplay = itemsToDisplay.filter(tab => tab !== 'incidentReport');
    }

    return itemsToDisplay.map((key) => {
      // Lazy loading: Only render tabs that have been visited at least once
      // This prevents all 8 tabs from rendering on initial load
      // Once mounted, tabs stay mounted but hidden to preserve state
      if (!mountedTabs.has(key)) {
        return null;
      }

      // Check if this is a custom tab from configuration
      const customTab = sessionDetailsConfig?.tabs?.find(
        tab => typeof tab === 'object' && tab.id === key
      );

      let component;

      // If custom tab with URL, use CustomTab component
      if (customTab && customTab.url) {
        component = <CustomTab tab={customTab} />;
      } else {
        // Use component from navOptions
        component = navOptions[key]?.component;
      }

      if (!component) return null;

      return (
        <div
          key={key}
          style={{
            display: selectedNavItem === key ? 'block' : 'none',
            height: '100%',
            overflow: 'auto',
          }}
        >
          {component}
        </div>
      );
    });
  }, [navOptions, selectedNavItem, sessionDetailsConfig, incidentReport, mountedTabs]);

  return (
    <Flex.Item
      as="div"
      overflowX="hidden"
      overflowY="hidden"
      shouldShrink
      shouldGrow
      height="100%"
    >

      <View
        as="div"
        position="relative"
        margin="none"
        padding="none"
        className="session-details"
        overflow="hidden"
        height="100%"
      >
        <NavBar
          navOptions={navOptions}
          selectedNavItem={selectedNavItem}
          onNavItemClick={handleNavItemClick}
          sessionDetailsConfig={sessionDetailsConfig}
          incidentReport={incidentReport}
          navigationHeight={navigationHeight}
        />
        {/* Load the component in this view... */}
        <View
          as="div"
          className="session-details-content-wrapper"
          position="relative"
          margin="none"
          padding="xx-small"
          //height={`${contentHeight}px`}
          height={`calc(100% - ${navigationHeight}px)`}
          overflowY="auto"
          overflowX="hidden"

        >
          {/* Render all components but only show the selected one */}
          {contentComponents}
        </View>
      </View >
    </Flex.Item >
  );
};

export default SessionDetails;
